import { isStylableLineSymbol, type LineSymbol, type LineSymbolShape } from '@ikegps/core-api';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { LineSwatch } from '../../components/line.swatch';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { CSS, PREVIEW_HEIGHT, PREVIEW_WIDTH, Style } from './line.symbol.customizer.style';

addStyles(Style);

export interface LineSymbolCustomizerAttrs {
	symbol: LineSymbol;
	/** Stroke shapes offered in the style grid (the freely-stylable ones). */
	shapes: readonly LineSymbolShape[];
	palette: readonly string[];
	/** Apply a new stroke shape, keeping the current color. */
	onPickShape: (symbolId: string) => void;
	/** Apply a new color, keeping the current shape. */
	onPickColor: (color: string) => void;
}

/** Presentational — pure props-in / callbacks-out, no observable state, so it ports cleanly to a React component later. */
export const LineSymbolCustomizer = {
	view(attrs: LineSymbolCustomizerAttrs): MithrilVNode {
		return m('div', { className: CSS.container }, [
			viewPreview(attrs.symbol),
			viewStyleSection(attrs),
			viewColorSection(attrs),
		]);
	},
};

function viewPreview(symbol: LineSymbol): MithrilVNode {
	return m('div', { className: CSS.section }, [
		m('div', { className: CSS.caption }, Translate(T.FIELD_LINE_PREVIEW)),
		m('div', { className: CSS.preview }, LineSwatch.view({ symbol, width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT })),
	]);
}

function viewStyleSection(attrs: LineSymbolCustomizerAttrs): MithrilVNode {
	// A symbol with an intrinsic glyph (utility code label / fence-railroad marker) is color-only.
	const stylable = isStylableLineSymbol(attrs.symbol.id);
	return m('div', { className: CSS.section }, [
		m('div', { className: CSS.caption }, Translate(T.FIELD_LINE_STYLE)),
		stylable ? viewShapeGrid(attrs) : viewFixedMessage(attrs.symbol),
	]);
}

function viewFixedMessage(symbol: LineSymbol): MithrilVNode {
	const message =
		symbol.label != null
			? Translate(T.FIELD_LINE_STYLE_FIXED_CODE, { label: symbol.label })
			: Translate(T.FIELD_LINE_STYLE_FIXED_MARKER);
	return m('div', { className: CSS.fixedMessage }, [Icon.view(MaterialIcon.LOCK), m('span', message)]);
}

function viewShapeGrid(attrs: LineSymbolCustomizerAttrs): MithrilVNode {
	const { symbol, shapes, onPickShape } = attrs;
	return m(
		'div',
		{ className: CSS.shapeGrid },
		shapes.map((shape) => {
			const active = shape.id === symbol.id;
			return m(
				'button',
				{
					type: 'button',
					className: active ? `${CSS.chip} ${CSS.chipActive}` : CSS.chip,
					title: shape.name,
					onclick: () => onPickShape(shape.id),
				},
				[
					m(
						'div',
						{ className: CSS.chipSwatch },
						LineSwatch.view({ symbol: { ...shape, color: symbol.color }, width: 120, height: 14 })
					),
					m('div', { className: CSS.chipLabel }, shape.name),
				]
			);
		})
	);
}

// Every dot gets a subtle inset edge (the desired "border"). The selected ring adds the dot's own
// color with a white gap (NOT the accent), set inline per dot so each ring matches its swatch.
const DOT_INSET = 'inset 0 0 0 1px rgba(0, 0, 0, 0.14)';
function dotShadow(color: string, active: boolean): string {
	return active ? `${DOT_INSET}, 0 0 0 2px #fff, 0 0 0 4px ${color}` : DOT_INSET;
}

function viewColorSection(attrs: LineSymbolCustomizerAttrs): MithrilVNode {
	const { symbol, palette, onPickColor } = attrs;
	const current = symbol.color.toLowerCase();
	const dots = palette.map((color) => {
		const active = color.toLowerCase() === current;
		return m(
			'button',
			{
				type: 'button',
				className: CSS.colorDot,
				title: color,
				style: { background: color, 'box-shadow': dotShadow(color, active) },
				onclick: () => onPickColor(color),
			},
			active ? Icon.view(MaterialIcon.CHECK) : null
		);
	});
	dots.push(viewCustomColorDot(attrs, current));
	return m('div', { className: CSS.section }, [
		m('div', { className: CSS.caption }, Translate(T.COLOR)),
		m('div', { className: CSS.colorWrap }, dots),
	]);
}

/**
 * Commit live on `input` so the swatch tracks the OS picker as it drags. Each commit is a MobX mutation,
 * but `RenderService` throttles the repaint to ~60fps and `setSymbol` is cheap — no re-render storm.
 */
function viewCustomColorDot(attrs: LineSymbolCustomizerAttrs, current: string): MithrilVNode {
	const { palette, onPickColor } = attrs;
	const isPreset = palette.some((color) => color.toLowerCase() === current);
	const glyphs = isPreset
		? [Icon.view(MaterialIcon.ADD)]
		: [
				Icon.view(MaterialIcon.CHECK, { className: CSS.customIconCheck }),
				Icon.view(MaterialIcon.EDIT, { className: CSS.customIconEdit }),
			];
	return m(
		'label',
		{
			className: isPreset
				? `${CSS.colorDot} ${CSS.customDot} ${CSS.customDotIdle}`
				: `${CSS.colorDot} ${CSS.customDot}`,
			title: Translate(T.FIELD_LINE_COLOR_CUSTOM),
			// Always set both explicitly so deselecting (off-palette → preset) clears the prior fill/ring.
			style: isPreset
				? { background: 'transparent', 'box-shadow': 'none' }
				: { background: current, 'box-shadow': dotShadow(current, true) },
		},
		[
			// Input FIRST so it stays the same DOM node across idle→selected (glyphs change count): if it
			// moved/recreated mid-pick the open OS picker would be torn down. It's absolute, so still on top.
			m('input', {
				type: 'color',
				className: CSS.customInput,
				value: current,
				oninput: (event: Event) => onPickColor((event.target as HTMLInputElement).value),
			}),
			...glyphs,
		]
	);
}
